/**
 * Telling "the request never reached us" apart from a real failure.
 *
 * On a phone, a locked screen or a switched app freezes the tab and kills every
 * in-flight request — axios reports `Network Error` with no response. Those are
 * not bugs: one iPhone mid-build produced ~110 PostHog issues in a week, every
 * one a chat save the server never saw (the server saves the turn itself).
 */
import { isAxiosError } from 'axios';

/** The request got no HTTP response at all — offline, frozen tab, dropped connection. */
export function isNetworkError(err: unknown): boolean {
  if (isAxiosError(err)) {
    return !err.response;
  }

  const message = err instanceof Error ? err.message : String(err ?? '');

  return /Network ?Error|Failed to fetch|Load failed/i.test(message);
}

/** Whether a request has a chance right now: the tab is in front and the device is online. */
export function canReachServer(): boolean {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return false;
  }

  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/**
 * Resolves once a request can go through again — immediately if it already can,
 * else on the tab becoming visible or the device coming back online.
 */
export function whenReachable(): Promise<void> {
  if (canReachServer()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const check = () => {
      if (canReachServer()) {
        document.removeEventListener('visibilitychange', check);
        window.removeEventListener('online', check);
        resolve();
      }
    };

    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
  });
}
